import { featureOperations, Features, hasFeature } from '@ardoq/features';
import { t, type I18nLocale } from '@ardoq/i18n';
import { getFeatureContext } from 'streams/featureContext$';

export type WorkspaceNavigationOption = {
  label: string;
  description: string;
  onClick: VoidFunction;
};

export const getWorkspaceNavigationOptions = ({
  i18nLocale,
  onOpenInInventory,
  onOpenInWorkspace,
  onOpenInViewpointMode,
}: {
  i18nLocale: I18nLocale;
  onOpenInInventory: VoidFunction;
  onOpenInWorkspace: VoidFunction;
  onOpenInViewpointMode?: VoidFunction;
}): WorkspaceNavigationOption[] => {
  const options: WorkspaceNavigationOption[] = [];
  // Snapshot read, same as the deprecated `hasFeature` calls below; this
  // helper is called from render code that has no featureContext$ in reach.
  const disableWorkspaceMode =
    featureOperations.isHardNaxEnabled(getFeatureContext());
  const preferInventory =
    disableWorkspaceMode ||
    hasFeature(Features.PREFER_OPENING_WORKSPACES_IN_INVENTORY);
  const preferWorkspace =
    !preferInventory &&
    hasFeature(Features.PREFER_OPENING_WORKSPACES_IN_WORKSPACE);
  if (!preferWorkspace) {
    options.push({
      label: t(
        i18nLocale,
        'assetsBrowser:workspaceNavigation.openInDataInventory.label',
        'Open in Data Inventory'
      ),
      description: t(
        i18nLocale,
        'assetsBrowser:workspaceNavigation.openInDataInventory.description',
        'Best for data editing (spreadsheet)'
      ),
      onClick: onOpenInInventory,
    });
  }
  if (!preferInventory) {
    options.push({
      label: t(
        i18nLocale,
        'assetsBrowser:workspaceNavigation.openInWorkspace.label',
        'Open in workspace'
      ),
      description: t(
        i18nLocale,
        'assetsBrowser:workspaceNavigation.openInWorkspace.description',
        'Legacy mode for data visualisation'
      ),
      onClick: onOpenInWorkspace,
    });
  }
  if (
    !disableWorkspaceMode &&
    hasFeature(Features.CANVAS_PROTOTYPE_EDGE) &&
    onOpenInViewpointMode
  ) {
    options.push({
      label: t(
        i18nLocale,
        'assetsBrowser:workspaceNavigation.openInCanvasPrototype.label',
        'Open in canvas prototype'
      ),
      description: t(
        i18nLocale,
        'assetsBrowser:workspaceNavigation.openInCanvasPrototype.description',
        'Prototype of the new canvas'
      ),
      onClick: onOpenInViewpointMode,
    });
  }
  return options;
};
